import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantProtection } from '../../hooks/useTenantProtection';
import { useTenantConfig } from '../../hooks/useTenantConfig';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Save,
  Eye,
  ArrowLeft,
  Settings,
  Plus,
  Table2,
  BarChart3,
  Type,
  Trash2,
  Copy,
  X,
  Grid3x3,
  ChevronLeft,
  ChevronRight,
  FileText,
  Image
} from 'lucide-react';
import SectionToolbox from './components/SectionToolbox';
import ReportCanvas from './components/ReportCanvas';
import SectionEditor from './components/SectionEditor';
import ParameterManager from './components/ParameterManager';
import ReportParameters from './components/ReportParameters';

const defaultGlobalStyles = {
  pageSize: 'A4',
  orientation: 'portrait',
  css: 'font-family: Arial, sans-serif;',
  headerFooter: {
    header: {
      enabled: true,
      companyName: '',
      showReportDate: true,
      logoDataUrl: ''
    },
    footer: {
      enabled: false,
      companyName: '',
      showReportDate: true,
      logoDataUrl: ''
    }
  }
};

const ReportBuilder = ({ mode = 'create' }) => {
  const { id } = useParams();
  const { user } = useAuth();
  const { getProtectedTenantCode } = useTenantProtection();
  const { tenantConfig } = useTenantConfig();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [reportName, setReportName] = useState('Untitled Report');
  const [reportDescription, setReportDescription] = useState('');
  const [parameters, setParameters] = useState([]);
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [showToolbox, setShowToolbox] = useState(true);
  const [showEditor, setShowEditor] = useState(true);
  const [showParametersPanel, setShowParametersPanel] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showHeaderFooterModal, setShowHeaderFooterModal] = useState(false);
  const [previewParameters, setPreviewParameters] = useState({});
  const [layoutConfig, setLayoutConfig] = useState({
    type: 'grid',
    columns: 12,
    gap: 16
  });
  const [globalStyles, setGlobalStyles] = useState(defaultGlobalStyles);

  useEffect(() => {
    if (mode === 'edit' && id) {
      loadReport();
    }
  }, [mode, id]);

  const mergeGlobalStyles = (styles) => {
    return {
      ...defaultGlobalStyles,
      ...styles,
      headerFooter: {
        ...defaultGlobalStyles.headerFooter,
        ...(styles?.headerFooter || {}),
        header: {
          ...defaultGlobalStyles.headerFooter.header,
          ...(styles?.headerFooter?.header || {})
        },
        footer: {
          ...defaultGlobalStyles.headerFooter.footer,
          ...(styles?.headerFooter?.footer || {})
        }
      }
    };
  };

  const loadReport = async () => {
    try {
      setLoading(true);
      const tenantCode = getProtectedTenantCode();
      const token = localStorage.getItem('token');

      const response = await fetch(`/api/reports/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Code': tenantCode
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load report');
      }

      const data = await response.json();
      const report = data.data;

      setReportName(report.ReportName);
      setReportDescription(report.Description || '');
      setParameters(report.ReportConfig?.parameters || []);
      setSections(report.ReportConfig?.sections || []);
      setLayoutConfig(report.ReportConfig?.layout || layoutConfig);
      setGlobalStyles(mergeGlobalStyles(report.ReportConfig?.globalStyles));
    } catch (error) {
      console.error('Error loading report:', error);
      toast.error('Failed to load report');
      navigate('/reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!tenantConfig) {
      return;
    }

    setGlobalStyles(prev => {
      let changed = false;
      const nextHeader = { ...(prev.headerFooter?.header || {}) };
      const nextFooter = { ...(prev.headerFooter?.footer || {}) };

      if (!nextHeader.companyName && tenantConfig.company_name) {
        nextHeader.companyName = tenantConfig.company_name;
        changed = true;
      }

      if (!nextFooter.companyName && tenantConfig.company_name) {
        nextFooter.companyName = tenantConfig.company_name;
        changed = true;
      }

      if (!changed) {
        return prev;
      }

      return {
        ...prev,
        headerFooter: {
          header: nextHeader,
          footer: nextFooter
        }
      };
    });
  }, [tenantConfig]);

  const handleSave = async () => {
    try {
      if (!reportName.trim()) {
        toast.error('Please enter a report name');
        return;
      }

      if (sections.length === 0) {
        toast.error('Please add at least one section to the report');
        return;
      }

      setSaving(true);
      const tenantCode = getProtectedTenantCode();
      const token = localStorage.getItem('token');

      const reportData = {
        name: reportName,
        description: reportDescription,
        config: {
          parameters: parameters,
          layout: layoutConfig,
          sections: sections,
          globalStyles: globalStyles
        },
        isActive: true
      };

      const url = mode === 'edit' ? `/api/reports/${id}` : '/api/reports';
      const method = mode === 'edit' ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Code': tenantCode,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(reportData)
      });

      if (!response.ok) {
        throw new Error('Failed to save report');
      }

      const data = await response.json();
      toast.success(mode === 'edit' ? 'Report updated successfully' : 'Report created successfully');

      if (mode === 'create') {
        navigate(`/reports/${data.data.ReportID}`);
      }
    } catch (error) {
      console.error('Error saving report:', error);
      toast.error('Failed to save report');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = () => {
    if (sections.length === 0) {
      toast.error('Please add at least one section to preview');
      return;
    }

    // Initialize preview parameters with defaults
    const initialParams = {};
    if (parameters && parameters.length > 0) {
      parameters.forEach(param => {
        if (param.defaultValue !== undefined && param.defaultValue !== null) {
          initialParams[param.name] = param.defaultValue;
        }
      });
    }
    setPreviewParameters(initialParams);
    setShowPreviewModal(true);
  };

  const handleAddSection = (sectionType) => {
    const newSection = {
      id: `section-${Date.now()}`,
      type: sectionType,
      position: {
        col: 0,
        row: sections.length * 2,
        width: 12,
        height: sectionType === 'text' ? 2 : 6
      },
      config: getDefaultSectionConfig(sectionType),
      styling: {
        css: '',
        className: ''
      }
    };

    setSections([...sections, newSection]);
    setSelectedSection(newSection.id);
  };

  const getDefaultSectionConfig = (type) => {
    switch (type) {
      case 'table':
        return {
          title: 'New Table',
          entity: 'customers',
          fields: [],
          groupBy: [],
          aggregations: [],
          filters: [],
          sorting: [],
          limit: null
        };
      case 'chart':
        return {
          title: 'New Chart',
          chartType: 'bar',
          entity: 'customers',
          groupBy: '',
          aggregation: {
            field: '',
            function: 'COUNT',
            alias: 'Count'
          },
          color: '#3b82f6',
          filters: [],
          sorting: []
        };
      case 'text':
        return {
          title: 'New Text Section',
          content: '<p>Enter your text content here...</p>',
          dataSource: null
        };
      default:
        return {};
    }
  };

  const handleUpdateSection = (sectionId, updates) => {
    setSections(sections.map(section =>
      section.id === sectionId ? { ...section, ...updates } : section
    ));
  };

  const handleDeleteSection = (sectionId) => {
    setSections(sections.filter(s => s.id !== sectionId));
    if (selectedSection === sectionId) {
      setSelectedSection(null);
    }
  };

  const handleDuplicateSection = (sectionId) => {
    const section = sections.find(s => s.id === sectionId);
    if (!section) return;

    const duplicated = {
      ...JSON.parse(JSON.stringify(section)),
      id: `section-${Date.now()}`,
      position: {
        ...section.position,
        row: section.position.row + section.position.height + 1
      }
    };

    setSections([...sections, duplicated]);
  };

  const handleSectionPositionChange = (sectionId, newPosition) => {
    handleUpdateSection(sectionId, { position: newPosition });
  };

  const updateHeaderFooterConfig = (section, updates) => {
    setGlobalStyles(prev => ({
      ...prev,
      headerFooter: {
        ...prev.headerFooter,
        [section]: {
          ...(prev.headerFooter?.[section] || {}),
          ...updates
        }
      }
    }));
  };

  const handleLogoUpload = (section, file) => {
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      updateHeaderFooterConfig(section, { logoDataUrl: reader.result });
    };
    reader.readAsDataURL(file);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading report...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-100 dark:bg-gray-900">
      {/* Header */}

      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white shadow-md px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 flex-1">
            <button
              onClick={() => navigate('/reports')}
              className="p-2 rounded-lg bg-white bg-opacity-20 hover:bg-opacity-30 transition-colors text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center justify-center w-10 h-10 bg-white bg-opacity-20 rounded-lg mr-2">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 max-w-md">
              <input
                type="text"
                value={reportName}
                onChange={(e) => setReportName(e.target.value)}
                placeholder="Report Name"
                className="w-full px-3 py-2 text-lg font-semibold border-b-2 border-transparent focus:border-white/50 bg-transparent outline-none text-white placeholder-blue-200 focus:bg-white/10 rounded transition-colors"
              />
              <input
                type="text"
                value={reportDescription}
                onChange={(e) => setReportDescription(e.target.value)}
                placeholder="Description (optional)"
                className="w-full px-3 py-1 text-sm text-blue-100 placeholder-blue-200 border-b border-transparent focus:border-white/50 bg-transparent outline-none focus:bg-white/10 rounded transition-colors"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowParametersPanel(!showParametersPanel)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${showParametersPanel
                ? 'bg-white text-teal-700 shadow-sm'
                : 'bg-white bg-opacity-20 hover:bg-opacity-30 text-white'
                }`}
            >
              <Settings className="w-4 h-4" />
              Parameters
              {parameters.length > 0 && (
                <span className="bg-teal-700 text-white text-xs px-2 py-0.5 rounded-full">
                  {parameters.length}
                </span>
              )}
            </button>
            <button
              onClick={handlePreview}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white bg-opacity-20 hover:bg-opacity-30 text-white transition-colors"
            >
              <Eye className="w-4 h-4" />
              Preview
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-teal-700 font-medium hover:bg-blue-50 shadow-sm transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : mode === 'edit' ? 'Update' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Section Toolbox */}
        <div className={`${showToolbox ? 'w-64' : 'w-12'} bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 flex flex-col`}>
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            {showToolbox && (
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                Sections
              </h3>
            )}
            <button
              onClick={() => setShowToolbox(!showToolbox)}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {showToolbox ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          </div>
          {showToolbox && (
            <div className="flex-1 overflow-y-auto p-4">
              <SectionToolbox onAddSection={handleAddSection} />
            </div>
          )}
        </div>

        {/* Center - Canvas */}
        <div className="flex-1 overflow-auto p-6">
          <ReportCanvas
            sections={sections}
            selectedSection={selectedSection}
            onSelectSection={setSelectedSection}
            onUpdateSection={handleUpdateSection}
            onDeleteSection={handleDeleteSection}
            onDuplicateSection={handleDuplicateSection}
            onPositionChange={handleSectionPositionChange}
            layoutConfig={layoutConfig}
            parameters={parameters}
          />
        </div>

        {/* Right Sidebar - Section Editor */}
        <div className={`${showEditor ? 'w-80' : 'w-12'} bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 transition-all duration-300 flex flex-col`}>
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            {showEditor && (
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                {selectedSection ? 'Section Properties' : 'Settings'}
              </h3>
            )}
            <button
              onClick={() => setShowEditor(!showEditor)}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {showEditor ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </button>
          </div>
          {showEditor && (
            <div className="flex-1 overflow-y-auto p-4">
              {selectedSection ? (
                <SectionEditor
                  section={sections.find(s => s.id === selectedSection)}
                  parameters={parameters}
                  onUpdate={(updates) => handleUpdateSection(selectedSection, updates)}
                />
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Page Size
                    </label>
                    <select
                      value={globalStyles.pageSize}
                      onChange={(e) => setGlobalStyles({ ...globalStyles, pageSize: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100"
                    >
                      <option value="A4">A4</option>
                      <option value="Letter">Letter</option>
                      <option value="Legal">Legal</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Orientation
                    </label>
                    <select
                      value={globalStyles.orientation}
                      onChange={(e) => setGlobalStyles({ ...globalStyles, orientation: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100"
                    >
                      <option value="portrait">Portrait</option>
                      <option value="landscape">Landscape</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Global CSS
                    </label>
                    <textarea
                      value={globalStyles.css}
                      onChange={(e) => setGlobalStyles({ ...globalStyles, css: e.target.value })}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-sm dark:bg-gray-700 dark:text-gray-100"
                      placeholder="font-family: Arial; color: #333;"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Header & Footer Template
                    </label>
                    <button
                      onClick={() => setShowHeaderFooterModal(true)}
                      className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                    >
                      <span className="flex items-center gap-2">
                        <Image className="w-4 h-4" />
                        Configure Header & Footer
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {globalStyles.headerFooter?.header?.enabled || globalStyles.headerFooter?.footer?.enabled
                          ? 'Enabled'
                          : 'Disabled'}
                      </span>
                    </button>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      Add logo, company name, and report date to exported files.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Parameters Panel Modal */}
      {showParametersPanel && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Manage Report Parameters
              </h2>
              <button
                onClick={() => setShowParametersPanel(false)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <ParameterManager
                parameters={parameters}
                onChange={setParameters}
              />
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowParametersPanel(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header/Footer Template Modal */}
      {showHeaderFooterModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Header & Footer Template
              </h2>
              <button
                onClick={() => setShowHeaderFooterModal(false)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-8">
              {['header', 'footer'].map(section => {
                const config = globalStyles.headerFooter?.[section] || {};
                const isHeader = section === 'header';
                return (
                  <div key={section} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 capitalize">
                        {section}
                      </h3>
                      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <input
                          type="checkbox"
                          checked={config.enabled || false}
                          onChange={(e) => updateHeaderFooterConfig(section, { enabled: e.target.checked })}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        Enable {section}
                      </label>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Company Name
                        </label>
                        <input
                          type="text"
                          value={config.companyName || ''}
                          onChange={(e) => updateHeaderFooterConfig(section, { companyName: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100"
                          placeholder="Company Name"
                        />
                      </div>
                      <div className="flex items-center gap-3 mt-6 md:mt-0">
                        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                          <input
                            type="checkbox"
                            checked={config.showReportDate || false}
                            onChange={(e) => updateHeaderFooterConfig(section, { showReportDate: e.target.checked })}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                          />
                          Show report date
                        </label>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Logo
                      </label>
                      <div className="flex flex-col md:flex-row md:items-center gap-4">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleLogoUpload(section, e.target.files?.[0])}
                          className="block w-full text-sm text-gray-600 dark:text-gray-300"
                        />
                        <button
                          type="button"
                          onClick={() => updateHeaderFooterConfig(section, { logoDataUrl: '' })}
                          className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                          Clear Logo
                        </button>
                      </div>
                      {config.logoDataUrl && (
                        <div className="mt-3 flex items-center gap-3">
                          <img
                            src={config.logoDataUrl}
                            alt={`${section} logo`}
                            className="h-10 w-auto max-w-[160px] rounded border border-gray-200 dark:border-gray-700"
                          />
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {isHeader ? 'Header' : 'Footer'} logo preview
                          </span>
                        </div>
                      )}
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Upload an image to embed in PDF/HTML exports.
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowHeaderFooterModal(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Preview Report: {reportName}
              </h2>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {parameters && parameters.length > 0 && (
                <div className="mb-6">
                  <ReportParameters
                    parameters={parameters}
                    values={previewParameters}
                    onChange={setPreviewParameters}
                  />
                </div>
              )}
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-8 text-center">
                <Eye className="w-16 h-16 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Preview functionality coming soon
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-500">
                  Save the report and open it in the Report Viewer for full preview with data
                </p>
                <button
                  onClick={handleSave}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Save Report
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportBuilder;

